/**
 * The landing page's invite code: where the field's first value comes from, and
 * what is said when the server refuses one.
 *
 * Pure, so both can be tested without a DOM. The server is the only judge of a
 * code — it upper-cases, checks the registry, and answers 403 for anything that
 * is not an open batch — so nothing here validates. An empty field is sent as
 * it is: a server started with `REQUIRE_INVITE=0` takes it, and one that is not
 * says so in words the field can show.
 */

/** Longest code the server accepts; a longer query value is not one. */
const MAX_INVITE_LENGTH = 24

/** What the server says when it has no words of its own. */
export const INVITE_NOT_OPEN = "That invite code isn't open. Check the code you were given."

/** Anything else that stopped a room opening. */
export const CREATE_FAILED = "Couldn't start a game just now. Try again."

/**
 * The field's starting value from `?invite=` on the landing URL, or `''`.
 *
 * A convenience for a link an experimenter hands out, not a decision: the value
 * is shown in the field, where the participant can see and change it, and the
 * server checks it when the game is created. Trimmed, and dropped if it could
 * not be a code at all, so a mangled link shows an empty field rather than junk.
 */
export function readInviteQuery(search: string): string {
  const raw = new URLSearchParams(search).get('invite')
  if (raw === null) return ''
  const value = raw.trim()
  return value.length <= MAX_INVITE_LENGTH ? value : ''
}

/**
 * The sentence to show under the invite field after a create request failed,
 * and whether it is about the code.
 *
 * A 403 is the invite check, and the server's own `error` is shown when it has
 * one. Anything else is not the participant's code to fix.
 */
export function createErrorFrom(status: number, body: unknown): { message: string; aboutInvite: boolean } {
  if (status === 403) {
    const error = (body as { error?: unknown } | null)?.error
    return {
      message: typeof error === 'string' && error.trim() !== '' ? error : INVITE_NOT_OPEN,
      aboutInvite: true,
    }
  }
  return { message: CREATE_FAILED, aboutInvite: false }
}
